import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Rocket, LineChart, Sparkles } from "lucide-react";

const DEFAULTS = {
  horizon_years: 5, initial_investment: 200000, starting_revenue: 300000,
  revenue_growth_pct: 15, gross_margin_pct: 50, opex: 60000, opex_growth_pct: 2,
  personnel_cost: 90000, personnel_growth_pct: 2, tax_rate_pct: 24,
  discount_rate_pct: 10, dso_days: 60, dio_days: 30, dpo_days: 60, depreciation_years: 5,
};

const FIELDS = [
  ["horizon_years", { it: "Orizzonte (anni)", en: "Horizon (years)" }],
  ["initial_investment", { it: "Investimento iniziale (CAPEX) €", en: "Initial investment (CAPEX) €" }],
  ["starting_revenue", { it: "Ricavi anno 1 €", en: "Year-1 revenue €" }],
  ["revenue_growth_pct", { it: "Crescita ricavi % annua", en: "Annual revenue growth %" }],
  ["gross_margin_pct", { it: "Margine lordo %", en: "Gross margin %" }],
  ["opex", { it: "Costi operativi anno 1 €", en: "Year-1 operating costs €" }],
  ["personnel_cost", { it: "Costo del personale anno 1 €", en: "Year-1 personnel cost €" }],
  ["tax_rate_pct", { it: "Aliquota fiscale %", en: "Tax rate %" }],
  ["discount_rate_pct", { it: "Tasso di sconto (WACC) %", en: "Discount rate (WACC) %" }],
  ["dso_days", { it: "Giorni incasso (DSO)", en: "Collection days (DSO)" }],
  ["dio_days", { it: "Giorni magazzino (DIO)", en: "Inventory days (DIO)" }],
  ["dpo_days", { it: "Giorni pagamento (DPO)", en: "Payment days (DPO)" }],
  ["depreciation_years", { it: "Anni ammortamento", en: "Depreciation years" }],
];

export default function NewBusinessPlan() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [company, setCompany] = useState("");
  const [mode, setMode] = useState("startup");
  const [sector, setSector] = useState("");
  const [description, setDescription] = useState("");
  const [linkedId, setLinkedId] = useState("");
  const [analyses, setAnalyses] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [a, setA] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/analyses").then((r) => setAnalyses(r.data)).catch(() => {});
    api.get("/sectors").then((r) => setSectors(r.data)).catch(() => {});
    const fromId = params.get("from");
    if (fromId) { setMode("existing"); setLinkedId(fromId); bridge(fromId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bridge = async (id) => {
    try {
      const { data } = await api.get(`/business-plans/bridge/${id}`);
      setA((prev) => ({ ...prev, ...data.assumptions }));
      if (data.company_name) setCompany((c) => c || data.company_name);
      if (data.sector) setSector(data.sector);
      toast.success(lang === "it" ? "Ipotesi precompilate dall'analisi" : "Assumptions pre-filled from analysis");
    } catch (e) { console.debug("BP bridge pre-fill failed (non-critical):", e); }
  };

  const onLink = (id) => { setLinkedId(id); if (id) bridge(id); };
  const setField = (k, v) => setA((p) => ({ ...p, [k]: v === "" ? "" : parseFloat(v) }));

  const submit = async () => {
    if (!company.trim()) { toast.error(lang === "it" ? "Inserisci il nome dell'impresa" : "Enter company name"); return; }
    setLoading(true);
    const assumptions = {};
    Object.keys(DEFAULTS).forEach((k) => { assumptions[k] = a[k] === "" || a[k] == null ? 0 : Number(a[k]); });
    try {
      const { data } = await api.post("/business-plans", {
        company_name: company, mode, language: lang, sector: sector || null,
        description: description || null,
        linked_analysis_id: mode === "existing" ? (linkedId || null) : null,
        assumptions,
      });
      navigate(`/business-plan/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || (lang === "it" ? "Errore nella creazione" : "Creation error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="App bg-slate-950 min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">{t("bp_new")}</h1>
        <p className="mt-2 text-slate-400">{lang === "it"
          ? "Motore finanziario (NPV, IRR, break-even) + piano narrativo generato con AI."
          : "Financial engine (NPV, IRR, break-even) + AI-generated narrative plan."}</p>

        <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-6">
          {/* Mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["startup", Rocket, lang === "it" ? "Startup (nessun bilancio)" : "Startup (no statements)"],
              ["existing", LineChart, lang === "it" ? "Da analisi esistente" : "From existing analysis"]].map(([m, Icon, label]) => (
              <button key={m} type="button" data-testid={`bp-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${mode === m ? "border-blue-500 bg-blue-950/40" : "border-slate-700 bg-slate-900 hover:border-slate-600"}`}>
                <Icon className={`h-5 w-5 ${mode === m ? "text-blue-400" : "text-slate-400"}`} />
                <span className="text-sm font-medium text-white">{label}</span>
              </button>
            ))}
          </div>

          {mode === "existing" && (
            <div className="space-y-1.5">
              <Label className="text-slate-300">{lang === "it" ? "Collega analisi bilancio" : "Link balance-sheet analysis"}</Label>
              <Select value={linkedId} onValueChange={onLink}>
                <SelectTrigger data-testid="bp-link-analysis"><SelectValue placeholder={lang === "it" ? "Seleziona analisi" : "Select analysis"} /></SelectTrigger>
                <SelectContent>
                  {analyses.map((an) => (
                    <SelectItem key={an.id} value={an.id}>{an.company_name} · {an.fiscal_year} · {an.grade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">{lang === "it" ? "Nome impresa" : "Company name"}</Label>
              <Input value={company} data-testid="bp-company" onChange={(e) => setCompany(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">{t("sector")}</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="bp-sector"><SelectValue placeholder={t("sector_placeholder")} /></SelectTrigger>
                <SelectContent>
                  {sectors.map((s) => (<SelectItem key={s.key} value={s.key}>{lang === "it" ? s.label_it : s.label_en}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300">{lang === "it" ? "Descrizione / idea imprenditoriale" : "Description / business idea"}</Label>
            <textarea value={description} data-testid="bp-description" onChange={(e) => setDescription(e.target.value)}
              rows={3} className="w-full rounded-md bg-slate-950 border border-slate-700 text-white text-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-blue-400 mb-3">
              {lang === "it" ? "Ipotesi finanziarie" : "Financial assumptions"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FIELDS.map(([k, lbl]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-slate-400 text-xs">{lbl[lang] || lbl.it}</Label>
                  <Input type="number" value={a[k]} data-testid={`bp-${k}`} onChange={(e) => setField(k, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <Button onClick={submit} disabled={loading} data-testid="bp-generate-btn" className="h-11 w-full sm:w-auto">
            <Sparkles className="h-4 w-4 mr-2" /> {loading ? (lang === "it" ? "Generazione…" : "Generating…") : (lang === "it" ? "Genera Business Plan" : "Generate Business Plan")}
          </Button>
        </div>
      </main>
    </div>
  );
}
